import { useRef, useState, useCallback } from 'react';
import { isSameDay } from 'date-fns';

type CacheEntry<T> = {
  data: T;
  timestamp: number;
};

export type CacheMap<T> = Record<number, CacheEntry<T | null>>;

type MemoryPromiseInfo<T> = {
  promise: Promise<T | null>;
  resolve: (value: T | null) => void;
  reject: (reason?: unknown) => void;
};

export interface UseBatchFetchOptions<T> {
  fetchFn: (ids: number[]) => Promise<Record<number, T | null>>;
  flushTimeout: number;
  flushQueueSize: number;
  maxCacheItems: number;
  isCacheValid?: (timestamp: number) => boolean;
}

function defaultIsCacheValid(timestamp: number): boolean {
  return isSameDay(timestamp, Date.now());
}

function cleanupIfTooLarge<T>(map: CacheMap<T>, maxSize: number): CacheMap<T> {
  const keys = Object.keys(map);
  if (keys.length <= maxSize) {
    return map;
  }

  keys.sort((a, b) => {
    return map[Number(a)].timestamp - map[Number(b)].timestamp;
  });

  const toRemoveCount = keys.length - maxSize;
  const removeThese = new Set(keys.slice(0, toRemoveCount));

  const newMap: CacheMap<T> = {};
  for (const [key, val] of Object.entries(map)) {
    if (!removeThese.has(key)) {
      newMap[Number(key)] = val;
    }
  }
  return newMap;
}

export function useBatchFetch<T>({
  fetchFn,
  flushTimeout,
  flushQueueSize,
  maxCacheItems,
  isCacheValid = defaultIsCacheValid
}: UseBatchFetchOptions<T>) {
  const [cacheMap, setCacheMap] = useState<CacheMap<T>>({});
  const timeoutRef = useRef<number | null>(null);
  const promiseMapRef = useRef<Record<number, MemoryPromiseInfo<T>>>({});
  const fetchQueueRef = useRef<Set<number>>(new Set());

  const cleanUpStaleEntries = useCallback(
    (oldMap: CacheMap<T>): CacheMap<T> => {
      const newMap: CacheMap<T> = {};
      for (const [key, entry] of Object.entries(oldMap)) {
        if (isCacheValid(entry.timestamp)) {
          newMap[Number(key)] = entry;
        }
      }
      return newMap;
    },
    [isCacheValid]
  );

  const flushQueue = useCallback(async () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    const idsToFetch = Array.from(fetchQueueRef.current);
    fetchQueueRef.current.clear();

    if (idsToFetch.length === 0) return;

    try {
      const fetchedData = await fetchFn(idsToFetch);

      setCacheMap(prev => {
        let newMap = cleanUpStaleEntries(prev);

        for (const id of idsToFetch) {
          newMap[id] = {
            data: fetchedData[id] ?? null,
            timestamp: Date.now()
          };
        }

        newMap = cleanupIfTooLarge(newMap, maxCacheItems);
        return newMap;
      });

      for (const id of idsToFetch) {
        if (promiseMapRef.current[id]) {
          promiseMapRef.current[id].resolve(fetchedData[id] ?? null);
          delete promiseMapRef.current[id];
        }
      }
    } catch (error) {
      for (const id of idsToFetch) {
        if (promiseMapRef.current[id]) {
          promiseMapRef.current[id].reject(error);
          delete promiseMapRef.current[id];
        }
      }
    }
  }, [fetchFn, cleanUpStaleEntries, maxCacheItems]);

  const fetch = useCallback(
    (ids: number[]): Promise<T[] | null> => {
      const result: (T | null)[] = [];
      const pendingPromises: Promise<T | null>[] = [];

      for (const id of ids) {
        const entry = cacheMap[id];
        if (entry && isCacheValid(entry.timestamp)) {
          result.push(entry.data);
        } else if (promiseMapRef.current[id]) {
          pendingPromises.push(promiseMapRef.current[id].promise);
        } else {
          let resolveFn!: (value: T | null) => void;
          let rejectFn!: (reason?: unknown) => void;

          const promise: Promise<T | null> = new Promise((resolve, reject) => {
            resolveFn = resolve;
            rejectFn = reject;
          });

          promiseMapRef.current[id] = { promise, resolve: resolveFn, reject: rejectFn };
          fetchQueueRef.current.add(id);
          pendingPromises.push(promise);
        }
      }

      if (fetchQueueRef.current.size >= flushQueueSize) {
        flushQueue();
      } else {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }

        timeoutRef.current = window.setTimeout(() => {
          flushQueue();
        }, flushTimeout);
      }

      if (pendingPromises.length === 0) {
        return Promise.resolve(result.filter(r => r !== null) as T[]);
      }

      return Promise.all(pendingPromises).then(results => {
        const allResults = [...result, ...results].filter(r => r !== null) as T[];
        return allResults.length > 0 ? allResults : null;
      });
    },
    [cacheMap, isCacheValid, flushQueueSize, flushQueue, flushTimeout]
  );

  return { fetch, cacheMap };
}
